import { format } from "date-fns";
import { LANGS } from "./langs";

export const CURRENCIES = ["EUR", "GBP", "USD", "CHF", "AED", "AUD", "CAD", "SEK", "NOK", "DKK", "PLN"];
export const TIMEZONES = ["Europe/London", "Europe/Rome", "Europe/Paris", "Europe/Madrid", "Europe/Berlin", "Europe/Zurich",
  "Europe/Amsterdam", "Europe/Lisbon", "Europe/Dublin", "Europe/Stockholm", "Asia/Dubai", "America/New_York",
  "America/Los_Angeles", "America/Toronto", "Australia/Sydney", "UTC"];
export const CONSENT_LANGS = LANGS;
export const GENDERS = ["female", "male", "other", "prefer_not"];
export const STATUS_STYLES = {
  pending_deposit: "bg-amber-500/15 text-amber-800 border-amber-500/30",
  confirmed: "bg-sky-500/15 text-sky-800 border-sky-500/30",
  arrived: "bg-violet-500/15 text-violet-800 border-violet-500/30",
  completed: "bg-emerald-500/15 text-emerald-800 border-emerald-500/30",
  cancelled: "bg-zinc-500/15 text-zinc-600 border-zinc-500/30 line-through",
  no_show: "bg-red-500/15 text-red-700 border-red-500/30",
};

export function money(amount, currency, lang = "en") {
  try {
    return new Intl.NumberFormat(lang === "it" ? "it-IT" : "en-GB", { style: "currency", currency: currency || "EUR" }).format(amount || 0);
  } catch (e) {
    return `${(amount || 0).toFixed(2)} ${currency}`;
  }
}

export const todayStr = () => format(new Date(), "yyyy-MM-dd");

export function hexToHsl(hex) {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex || "");
  if (!m) return null;
  let [r, g, b] = [m[1], m[2], m[3]].map((x) => parseInt(x, 16) / 255);
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    h = max === r ? (g - b) / d + (g < b ? 6 : 0) : max === g ? (b - r) / d + 2 : (r - g) / d + 4;
    h /= 6;
  }
  return [Math.round(h * 360), Math.round(s * 100), Math.round(l * 100)];
}

function setIcons(href) {
  document.querySelectorAll("link[rel='icon'], link[rel='apple-touch-icon']").forEach((l) => { l.href = href; });
}

// Home-screen icon: logo on a white square; white parts of a light logo turn black (iOS shows transparent logos as plain white).
function brandIcon(logo) {
  const img = new Image();
  img.onload = () => {
    try {
      const probe = document.createElement("canvas");
    probe.width = img.naturalWidth; probe.height = img.naturalHeight;
    const pc = probe.getContext("2d");
    pc.drawImage(img, 0, 0);
    const data = pc.getImageData(0, 0, probe.width, probe.height);
    const px = data.data;
    let sum = 0, n = 0, clear = 0;
    for (let i = 0; i < px.length; i += 4) {
      if (px[i + 3] > 128) { sum += 0.299 * px[i] + 0.587 * px[i + 1] + 0.114 * px[i + 2]; n++; } else clear++;
    }
    if (clear > n * 0.05 && n && sum / n > 170) {
      for (let i = 0; i < px.length; i += 4) {
        const mx = Math.max(px[i], px[i + 1], px[i + 2]), mn = Math.min(px[i], px[i + 1], px[i + 2]);
        if (mn > 150 && mx - mn < 40) { px[i] = 20; px[i + 1] = 20; px[i + 2] = 20; }
      }
      pc.putImageData(data, 0, 0);
    }
    const S = 180, pad = 22, c = document.createElement("canvas");
    c.width = S; c.height = S;
    const ctx = c.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, S, S);
    const k = Math.min((S - 2 * pad) / img.naturalWidth, (S - 2 * pad) / img.naturalHeight);
    const w = img.naturalWidth * k, h = img.naturalHeight * k;
    ctx.drawImage(probe, (S - w) / 2, (S - h) / 2, w, h);
    setIcons(c.toDataURL("image/png"));
    } catch { setIcons(logo); }
  };
  img.onerror = () => setIcons(logo);
  if (!logo.startsWith("data:")) img.crossOrigin = "anonymous";
  img.src = logo;
}

export function applyBranding(b = {}) {
  const root = document.documentElement.style;
  const p = hexToHsl(b.primary) || [321, 37, 26];
  const a = hexToHsl(b.accent) || [344, 53, 68];
  if (b.clinic_name) document.title = b.clinic_name;
  if (b.clinic_name && b.logo) brandIcon(b.logo);
  else if (b.clinic_name) setIcons("/icon-192.png");
  root.setProperty("--primary", `${p[0]} ${p[1]}% ${p[2]}%`);
  root.setProperty("--ring", `${p[0]} ${p[1]}% ${p[2]}%`);
  root.setProperty("--primary-foreground", p[2] > 65 ? "220 15% 12%" : "0 0% 100%");
  root.setProperty("--brand-accent", `${a[0]} ${a[1]}% ${a[2]}%`);
  root.setProperty("--chart-1", `${p[0]} ${p[1]}% ${p[2]}%`);
  root.setProperty("--chart-2", `${a[0]} ${a[1]}% ${a[2]}%`);
  root.setProperty("--radius", `${b.radius ?? 0.75}rem`);
  const style = b.sidebar_style || "dark";
  if (style === "light") {
    root.setProperty("--sidebar-bg", "0 0% 100%");
    root.setProperty("--sidebar-fg", "220 15% 15%");
    root.setProperty("--sidebar-muted", "220 8% 45%");
  } else if (style === "brand") {
    root.setProperty("--sidebar-bg", `${p[0]} ${p[1]}% ${Math.max(p[2] - 6, 10)}%`);
    root.setProperty("--sidebar-fg", "0 0% 100%");
    root.setProperty("--sidebar-muted", "0 0% 100% / 0.72");
  } else {
    root.setProperty("--sidebar-bg", `${p[0]} 8% 13%`);
    root.setProperty("--sidebar-fg", "40 20% 92%");
    root.setProperty("--sidebar-muted", `${p[0]} 5% 64%`);
  }
  root.setProperty("--sidebar-active", style === "brand" ? `${a[0]} ${a[1]}% ${a[2]}%` : `${p[0]} ${p[1]}% ${p[2]}%`);
}

export function waLink(phone, text) {
  const digits = (phone || "").replace(/[^\d]/g, "");
  return `https://wa.me/${digits}?text=${encodeURIComponent(text)}`;
}

export function gcalLink({ title, startUtc, minutes, location }) {
  const s = new Date(startUtc);
  const e = new Date(s.getTime() + minutes * 60000);
  const f = (d) => d.toISOString().replace(/[-:]/g, "").split(".")[0] + "Z";
  return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(title)}&dates=${f(s)}/${f(e)}&location=${encodeURIComponent(location || "")}`;
}

export const shrinkImage = (file, max = 1200) => new Promise((resolve, reject) => {
  const img = new Image();
  img.onload = () => {
    const k = Math.min(1, max / img.width);
    const cv = document.createElement("canvas");
    cv.width = Math.round(img.width * k); cv.height = Math.round(img.height * k);
    cv.getContext("2d").drawImage(img, 0, 0, cv.width, cv.height);
    resolve(cv.toDataURL("image/jpeg", 0.82));
  };
  img.onerror = reject;
  img.src = URL.createObjectURL(file);
});

